import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  createPremiumSession,
  createPremiumSessionWithDetails,
  createSession,
  getCustomerPortal,
  getPremiumProductPrice,
  getProductPrice,
  getSubscriptionSessionById,
} from './StripeApi';
import { IUser } from '../UserSlice';
import { setAuthUserToLocalStorage } from '../../helper';

export interface ICheckoutSession {
  id: string;
  url: string;
}

export interface IProductPrice {
  id?: string;
  unitAmount: number;
  currency: string;
}

export interface ISubscriptionSession {
  user: IUser;
  sessionId: string;
  subscriptionId?: string;
  url: string;
  customerId: string;
  amount: number;
  currency: string;
  status: string;
  interval?: string;
  intervalCount?: number;
}

interface IStripeState {
  status: 'idle' | 'loading' | 'rejected';
  checkoutSession: ICheckoutSession | undefined;
  productPrice: IProductPrice | undefined;
  premiumPricesList: IProductPrice[];
  subscriptionSession: ISubscriptionSession | undefined;
}

const initialState: IStripeState = {
  status: 'idle',
  checkoutSession: undefined,
  productPrice: undefined,
  premiumPricesList: [],
  subscriptionSession: undefined,
};

export const StripeSlice = createSlice({
  name: 'stripe',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create DA Checkout Session
      .addCase(createSession.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.status = 'idle';
        state.checkoutSession = action.payload;
        if (action.payload.url) {
          window.location.href = action.payload.url;
        }
      })
      .addCase(createSession.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Product Price
      .addCase(getProductPrice.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getProductPrice.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productPrice = action.payload;
      })
      .addCase(getProductPrice.rejected, (state) => {
        state.status = 'rejected';
      })

      // Create Premium Checkout Session
      .addCase(createPremiumSession.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createPremiumSession.fulfilled, (state, action) => {
        state.status = 'idle';
        state.checkoutSession = action.payload;
        if (action.payload.url) {
          window.location.href = action.payload.url;
        }
      })
      .addCase(createPremiumSession.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Premium Product Price
      .addCase(getPremiumProductPrice.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPremiumProductPrice.fulfilled, (state, action) => {
        state.status = 'idle';
        state.premiumPricesList = action.payload;
      })
      .addCase(getPremiumProductPrice.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Premium Subscription Session
      .addCase(getSubscriptionSessionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSubscriptionSessionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subscriptionSession = action.payload;
      })
      .addCase(getSubscriptionSessionById.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Customer Portal
      .addCase(getCustomerPortal.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCustomerPortal.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.url) {
          window.location.href = action.payload.url;
        }
      })
      .addCase(getCustomerPortal.rejected, (state) => {
        state.status = 'rejected';
      })

      // Save User Details & Create Premium Checkout Session
      .addCase(createPremiumSessionWithDetails.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createPremiumSessionWithDetails.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.user) {
          setAuthUserToLocalStorage(action.payload.user);
        }
        if (action.payload.session) {
          window.location.href = action.payload.session.url;
        }
      })
      .addCase(createPremiumSessionWithDetails.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const getStripeState = (state: RootState): RootState['stripe'] =>
  state.stripe;

export * from './StripeApi';
export default StripeSlice.reducer;
